/* ============================================================
   ProviderDeliverySettingsCard — TASK-202 / 138b4149: the
   performer's delivery settings. Minimal first cut: the sender
   postal index (index_from) — without it index-based live
   tariffs (Почта России) decline in-band and customers see the
   static estimate — plus the sender street address with DaData
   suggestions (73644630). Module flags are echoed unchanged (their
   toggles live in the carrier settings surfaces).
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { DeliverySettings } from '../types/provider';
import { AddressInput } from './AddressInput';

/** Backend DeliveryModules bitmask (numeric JSON serialization). */
const MODULE_CDEK = 1;
const MODULE_RUSSIANPOST = 2;
const MODULE_OZON = 4;

export function ProviderDeliverySettingsCard() {
  const [settings, setSettings] = useState<DeliverySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [index, setIndex] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await api.getDeliverySettings();
      setSettings(s);
      setIndex(s.senderPostalCode ?? '');
      setSenderAddress(s.senderAddress ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      // Patch contract: modules echoed unchanged; '' clears the index.
      const s = await api.updateDeliverySettings({
        modules: settings?.modules ?? 0,
        senderPostalCode: index.trim(),
        senderAddress: senderAddress.trim(),
      });
      setSettings(s);
      setIndex(s.senderPostalCode ?? '');
      setSenderAddress(s.senderAddress ?? '');
      setSavedNote(s.senderPostalCode
        ? `Индекс отправителя сохранён: ${s.senderPostalCode}`
        : 'Индекс отправителя очищен');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const trimmed = index.trim();
  // 6 digits (RU index) or empty (clear); anything else is not submittable.
  const canSave = trimmed === '' || /^\d{6}$/.test(trimmed);
  const trimmedAddress = senderAddress.trim();
  // 73644630: the address needs ≥5 chars when non-empty (mirrors the backend).
  const addressValid = trimmedAddress === '' || trimmedAddress.length >= 5;
  const dirty = trimmed !== (settings?.senderPostalCode ?? '')
    || trimmedAddress !== (settings?.senderAddress ?? '');

  const chips = settings
    ? [
        { label: 'СДЭК', on: (settings.modules & MODULE_CDEK) !== 0 },
        { label: 'Почта России', on: (settings.modules & MODULE_RUSSIANPOST) !== 0 },
        { label: 'Ozon', on: (settings.modules & MODULE_OZON) !== 0 },
      ]
    : [];

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)', maxWidth: 560 }}>
      <div>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Доставка</h2>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Индекс отправителя включает живые тарифы Почты России: без него покупателям
          показывается статическая оценка доставки.
        </p>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : (
        <>
          {/* Read-only module chips — the source of truth for carrier flags. */}
          {chips.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {chips.map((c) => (
                <span key={c.label} className={c.on ? 'badge badge-success' : 'badge'}>
                  {c.label}
                </span>
              ))}
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
            <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
              Индекс отправителя (Почта России)
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={index}
              onChange={(e) => setIndex(e.target.value)}
              placeholder="190000"
              autoComplete="postal-code"
              maxLength={6}
              style={inputStyle}
            />
            <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
              Почтовый индекс, откуда отправляете посылки — ровно 6 цифр.
              Пустое поле = индекс не задан.
            </span>
          </label>

          {/* 73644630: sender street address — DaData-suggested (debounced
              proxy /api/v1/addresses/suggest; the API key stays server-side). */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
            <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
              Адрес отправителя
            </span>
            <AddressInput
              value={senderAddress}
              onChange={setSenderAddress}
              placeholder="Город, улица, дом — откуда отправляете"
            />
            <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
              Полный адрес для накладных курьерских служб и Почты.
              Начните вводить — подсказки появятся автоматически. Пустое поле = не задан.
            </span>
            {!addressValid && (
              <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-danger)' }}>
                Адрес отправителя — минимум 5 символов (или пусто, чтобы очистить).
              </span>
            )}
          </label>

          {error && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
          )}
          {savedNote && !error && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-success)' }}>{savedNote}</p>
          )}

          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !canSave || !addressValid || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};
